import { Fragment, useMemo } from 'react';

import {
  FilterAppliedProps,
  FilterComparer,
  FilterConfig,
  FilterMenuProps,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconAssigned } from '@components/icons/assigned';

import { useAuthenticatedUser } from 'features/authentication';
import { useStore } from 'models/Provider';
import { IJob } from 'models/types';

import { FilterActionButtons } from './filter-action-buttons';
import { FilterComparerMenu } from './filter-comparer-menu';
import { toSortedUserOptions } from './user-options';

const ASSIGNEE_COMPARERS: Array<FilterComparer> = ['is', 'not_is', 'any_of', 'none_of'];

type ExtraProps = {
  users: Array<{ value: string; label: string }>;
};

function FilterApplicationAssigneeApplied({ data }: FilterAppliedProps<ExtraProps>) {
  const { t, userStore } = useStore();

  function assigneeToString(assignee: string | number) {
    if (assignee === 'none' || assignee === 'anyone') {
      return t(`applicants.filters.assigned.${assignee}`);
    }
    return userStore.items.get(assignee).name;
  }

  const valueLabel = Array.isArray(data.value)
    ? data.value.map(assigneeToString)
    : assigneeToString(data.value!);

  return (
    <Filters.AppliedFilter
      icon={<IconAssigned />}
      category={t('applicants.filters.assigned.title')}
      value={valueLabel}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

function FilterApplicationAssigneeMenu({ users }: FilterMenuProps<ExtraProps>) {
  const { t } = useStore();
  const { state, actions, isMultiComparer, hasValue } = useCurrentFilter();
  const me = useAuthenticatedUser();

  const filterValueItems = useMemo(() => {
    if (state.comparer !== 'is') return users;

    return [
      { label: t('applicants.filters.assigned.none'), value: 'none' },
      { label: t('applicants.filters.assigned.anyone'), value: 'anyone' },
    ].concat(users);
  }, [t, users, state.comparer]);

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.assigned.title')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      {state.comparer && (
        <Filters.ListItem
          heading
          label={t('comparers.' + state.comparer).replace('...', '')}
          icon={<IconArrowLeft />}
          onClick={actions.unselectComparer}
        />
      )}

      <Filters.ListSeparator />

      {!state.comparer && (
        <Fragment>
          <Filters.ListItem
            label={t('applicants.filters.assigned.me')}
            onClick={() => {
              actions.applyFilter({ comparer: 'is', value: String(me.id) });
            }}
          />

          <Filters.ListItem
            label={t('applicants.filters.assigned.none')}
            onClick={() => {
              actions.applyFilter({ comparer: 'is', value: 'none' });
            }}
          />

          <Filters.ListItem
            label={t('applicants.filters.assigned.anyone')}
            onClick={() => {
              actions.applyFilter({ comparer: 'is', value: 'anyone' });
            }}
          />

          <Filters.ListSeparator />

          <FilterComparerMenu
            comparers={ASSIGNEE_COMPARERS}
            onComparerSelect={actions.selectComparer}
          />
        </Fragment>
      )}

      {state.comparer && (
        <Fragment>
          <Filters.FilterValueMenu
            items={filterValueItems}
            isMulti={isMultiComparer}
            onSetValue={actions.selectValue}
            state={state}
          />
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}
    </Filters.List>
  );
}

export function useFilterApplicationAssignee(job?: IJob): FilterConfig<ExtraProps> {
  const { t, userStore } = useStore();

  return useMemo(() => {
    const users = job ? job.combinedFollowers : userStore.list;
    const sortedUsers = toSortedUserOptions(users);

    return {
      category: 'assigned',
      label: t('applicants.filters.assigned.title'),
      icon: <IconAssigned />,
      extraProps: {
        users: sortedUsers,
      },
      AppliedFilter: FilterApplicationAssigneeApplied,
      Menu: FilterApplicationAssigneeMenu,
      toQueryData: (filter) => {
        switch (filter.comparer) {
          case 'is':
            if (filter.value === 'anyone') return { assigned_to__isnull: false };
            if (filter.value === 'none') return { assigned_to__isnull: true };
            return { assigned_to__term: filter.value };
          case 'any_of':
            return {
              assigned_to__terms: Array.isArray(filter.value)
                ? filter.value.join('__')
                : filter.value,
            };
          case 'not_is':
            return { assigned_to__exclude: filter.value };
          case 'none_of':
            return { assigned_to__exclude: filter.value };
          default:
            return {};
        }
      },
    };
  }, [job, t, userStore.list]);
}
